import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Users, Maximize, MoveVertical, Plug, Check, ChevronLeft, ChevronRight,
  ZoomIn, CalendarRange, Truck, ShieldCheck, Sparkles, Cake, Clock, HelpCircle, ShieldQuestion, Weight, Ruler, ShoppingCart,
} from "lucide-react";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import HuurModal from "../components/HuurModal";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { certificates } from "../mock";
import { getProduct, getSettings } from "../api";
import { useSeo } from "../hooks/useSeo";

const usps = [
  { icon: Truck, title: "Levering & opbouw", text: "Wij brengen, plaatsen veilig en halen weer op." },
  { icon: ShieldCheck, title: "Veilig & gekeurd", text: "Schoon materiaal met grondzeil en verankering." },
  { icon: Sparkles, title: "Voor elk feest", text: "Van verjaardagen tot school- en bedrijfsevents." },
];

const SpecRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
    <span className="flex items-center gap-2.5 text-slate-500"><Icon className="w-4 h-4 text-brand-blue" /> {label}</span>
    <span className="font-display text-brand-navy">{value}</span>
  </div>
);

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [huurOpen, setHuurOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [freeRadius, setFreeRadius] = useState(25);
  const [originLabel, setOriginLabel] = useState("Beverwijk");

  useEffect(() => {
    getSettings().then((s) => {
      if (s.delivery_free_radius_km != null) setFreeRadius(s.delivery_free_radius_km);
      if (s.delivery_origin_label) setOriginLabel(s.delivery_origin_label);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true); setActive(0);
    getProduct(slug).then((d) => { setProduct(d); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  useSeo({
    title: product ? `${product.name} huren | Jump en Joy` : "Springkussen huren | Jump en Joy",
    description: product
      ? `${product.name} huren voor ${product.price} euro per dag. ${product.tagline}. Levering, opbouw en ophalen inbegrepen. Reserveer direct online.`
      : "Huur dit springkussen direct online bij Jump en Joy. Levering, opbouw en ophalen inbegrepen.",
    keywords: product ? `${product.name} huren, ${product.category} huren, springkussen huren` : "springkussen huren",
  });

  if (loading) {
    return <div className="wrap py-20"><div className="h-96 rounded-3xl bg-slate-100 animate-pulse" /></div>;
  }
  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-2xl text-brand-navy">Springkussen niet gevonden</h1>
        <Link to="/onze-luchtkastelen" className="inline-flex mt-6 text-brand-blue font-bold">Terug naar het aanbod</Link>
      </div>
    );
  }

  const images = product.images && product.images.length ? product.images : [product.image];
  const move = (dir) => setActive((a) => (a + dir + images.length) % images.length);

  return (
    <div className="wrap py-10">
      <Link to="/onze-luchtkastelen" className="inline-flex items-center gap-2 text-brand-blue font-bold hover:gap-3 transition-all">
        <ArrowLeft className="w-4 h-4" /> Terug naar het aanbod
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-8">
        {/* Gallery carousel */}
        <div className="animate-fade-up">
          <Carousel className="rounded-3xl overflow-hidden shadow-lg ring-1 ring-slate-100">
            <CarouselContent>
              {images.map((img, i) => (
                <CarouselItem key={i}>
                  <button onClick={() => { setActive(i); setLightbox(true); }} className="block w-full bg-gradient-to-br from-brand-bluelight to-slate-50 group">
                    <img src={img} alt={product.name} className={product.has_vullingen ? "w-full aspect-[4/3] object-contain p-4" : "w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500"} />
                    <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center text-brand-blue shadow"><ZoomIn className="w-4 h-4" /></span>
                  </button>
                </CarouselItem>
              ))}
            </CarouselContent>
            {images.length > 1 && (<><CarouselPrevious className="left-3 w-11 h-11 !bg-brand-blue hover:!bg-brand-bluedark !text-white !border-0 shadow-md transition-all" /><CarouselNext className="right-3 w-11 h-11 !bg-brand-blue hover:!bg-brand-bluedark !text-white !border-0 shadow-md transition-all" /></>)}
          </Carousel>
          {images.length > 1 && (
            <div className="flex gap-2.5 mt-3">
              {images.map((img, i) => (
                <button key={i} onClick={() => { setActive(i); setLightbox(true); }} className="w-20 h-16 rounded-xl overflow-hidden ring-2 ring-transparent hover:ring-brand-blue opacity-80 hover:opacity-100 transition-all">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="animate-fade-up">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-block bg-brand-blue text-white text-xs font-bold rounded-full px-4 py-1.5">{product.category}</span>
            <span data-testid="detail-badge-nieuw" className="inline-flex items-center gap-1 bg-brand-green text-white text-xs font-bold rounded-full px-4 py-1.5"><Sparkles className="w-3.5 h-3.5" /> Gloednieuw</span>
          </div>
          <h1 className="font-display text-brand-navy text-4xl mt-3">{product.name}</h1>
          <p className="text-brand-blue font-display text-lg mt-2">{product.tagline}</p>

          <div className="flex flex-wrap items-center gap-3 mt-5">
            <span className="font-display text-3xl text-brand-blue">€{product.price} <span className="text-base font-semibold text-slate-400">/ dag</span></span>
            <span className="text-sm text-slate-400 font-semibold">excl. bezorgkosten</span>
          </div>

          {/* Certificate badge (not a button) with tooltip - hidden for food machines */}
          {!product.has_vullingen && (
          <TooltipProvider delayDuration={100}>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {certificates.slice(0, 2).map((c) => (
                <span key={c.name} className="inline-flex items-center gap-1.5 rounded-full bg-brand-bluelight text-brand-blue text-sm font-semibold px-3 py-1.5">
                  <ShieldCheck className="w-4 h-4" /> {c.name}
                  <Tooltip>
                    <TooltipTrigger asChild><button type="button" className="text-brand-blue/70 hover:text-brand-blue"><HelpCircle className="w-4 h-4" /></button></TooltipTrigger>
                    <TooltipContent className="max-w-[220px] text-center">{c.info}</TooltipContent>
                  </Tooltip>
                </span>
              ))}
            </div>
          </TooltipProvider>
          )}

          <div className="flex flex-wrap gap-3 mt-6">
            <button onClick={() => setHuurOpen(true)} data-testid="huur-nu-button-top" className="rounded-full bg-brand-pink hover:bg-brand-pinkdark text-white font-bold px-7 py-3.5 transition-colors shadow-md inline-flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" /> Huur nu
            </button>
          </div>

          {product.has_vullingen ? (
            <div data-testid="delivery-notice-food" className="mt-4 inline-flex items-center gap-2.5 rounded-2xl bg-brand-bluelight text-brand-blue px-4 py-2.5 text-sm font-semibold">
              <Truck className="w-4 h-4 shrink-0" />
              Bezorgkosten worden berekend in de winkelwagen
            </div>
          ) : (
            <div data-testid="free-delivery-notice" className="mt-4 inline-flex items-center gap-2.5 rounded-2xl bg-brand-greenlight text-brand-green px-4 py-2.5 text-sm font-semibold">
              <Truck className="w-4 h-4 shrink-0" />
              Gratis bezorging binnen {Number(freeRadius).toString().replace(".", ",")} km rondom {originLabel}
            </div>
          )}

          {/* Details dropdowns */}
          <Accordion type="single" collapsible defaultValue="beschrijving" className="mt-7">
            <AccordionItem value="beschrijving" className="border border-slate-100 rounded-2xl px-4 mb-3 data-[state=open]:border-brand-blue/40">
              <AccordionTrigger className="font-display text-brand-navy hover:no-underline">Beschrijving</AccordionTrigger>
              <AccordionContent className="text-slate-500 leading-relaxed">{product.description}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="specs" className="border border-slate-100 rounded-2xl px-4 mb-3 data-[state=open]:border-brand-blue/40">
              <AccordionTrigger className="font-display text-brand-navy hover:no-underline">Specificaties</AccordionTrigger>
              <AccordionContent>
                <SpecRow icon={Users} label="Capaciteit" value={product.capacity} />
                <SpecRow icon={Maximize} label="Afmeting" value={product.size} />
                <SpecRow icon={MoveVertical} label="Hoogte" value={product.height} />
                {product.gewicht && <SpecRow icon={Weight} label="Gewicht" value={product.gewicht} />}
                {product.max_gebruiker && <SpecRow icon={Ruler} label="Max. lengte gebruiker" value={product.max_gebruiker} />}
                <SpecRow icon={Plug} label="Stroom" value={product.power} />
                {product.opzettijd && <SpecRow icon={Clock} label="Opzetten" value={product.opzettijd} />}
                {product.leeftijd && <SpecRow icon={Cake} label="Leeftijd" value={product.leeftijd} />}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="inbegrepen" className="border border-slate-100 rounded-2xl px-4 data-[state=open]:border-brand-blue/40">
              <AccordionTrigger className="font-display text-brand-navy hover:no-underline">Inbegrepen</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2">
                  {product.features.map((f) => (
                    <span key={f} className="flex items-center gap-2 text-brand-navy"><Check className="w-4 h-4 text-brand-green" /> <span className="text-sm">{f}</span></span>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="flex items-start gap-2 mt-4 text-sm text-slate-500 bg-slate-50 rounded-xl px-4 py-3">
            <ShieldQuestion className="w-4 h-4 text-brand-blue mt-0.5 shrink-0" />
            <span>Tot <b className="text-brand-navy">48 uur</b> vooraf <b className="text-brand-navy">kosteloos</b> annuleren · tot <b className="text-brand-navy">24 uur</b> vooraf <b className="text-brand-navy">50% terugbetaling</b>.</span>
          </div>
        </div>
      </div>

      {/* USP strip (per product page) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
        {usps.map((u) => (
          <div key={u.title} className="rounded-2xl bg-brand-bluelight p-5 flex gap-3">
            <div className="w-11 h-11 rounded-xl bg-brand-blue flex items-center justify-center shrink-0"><u.icon className="w-5 h-5 text-white" /></div>
            <div>
              <h3 className="font-display text-brand-navy">{u.title}</h3>
              <p className="text-slate-500 text-sm mt-0.5 leading-snug">{u.text}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Availability */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-14">
        <div className="rounded-3xl border-2 border-brand-bluelight shadow-sm p-5 sm:p-7">
          <h2 className="font-display text-2xl text-brand-navy mb-5 flex items-center gap-2"><CalendarRange className="w-6 h-6 text-brand-blue" /> Beschikbaarheid</h2>
          <AvailabilityCalendar bookedDates={product.bookedDates || []} />
        </div>
        <div className="rounded-3xl bg-brand-blue text-white p-6 sm:p-8 flex flex-col justify-center relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10" />
          <div className="absolute -bottom-12 -left-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 bg-brand-pink text-white text-xs font-bold rounded-full px-4 py-1.5"><CalendarRange className="w-3.5 h-3.5" /> Direct online boeken</span>
            <h3 className="font-display text-3xl mt-4">Datum vrij? Huur 'm nu!</h3>
            <p className="text-blue-50 mt-3 leading-relaxed">
              Kies je datum in de agenda en reserveer in één minuut. Betaal met iDEAL, Contant of pin op locatie.
            </p>
            <div className="flex flex-wrap gap-3 mt-4 text-sm">
              <span className="inline-flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1.5"><Check className="w-4 h-4" /> Levering inbegrepen</span>
              <span className="inline-flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1.5"><Clock className="w-4 h-4" /> Opzetten {product.opzettijd}</span>
            </div>
            <button onClick={() => setHuurOpen(true)} data-testid="huur-nu-button-bottom" className="inline-flex items-center gap-2 mt-6 rounded-full bg-white text-brand-blue font-bold px-8 py-4 text-lg hover:bg-slate-100 hover:-translate-y-0.5 transition-all shadow-lg">
              Huur nu <ArrowLeft className="w-5 h-5 rotate-180" />
            </button>
          </div>
        </div>
      </div>

      <HuurModal product={product} open={huurOpen} onOpenChange={setHuurOpen} />

      {/* Lightbox */}
      <Dialog open={lightbox} onOpenChange={setLightbox}>
        <DialogContent className="max-w-3xl bg-black/95 border-0 p-2 [&>button]:!bg-brand-blue [&>button]:hover:!bg-brand-bluedark [&>button]:!text-white [&>button]:w-11 [&>button]:h-11 [&>button]:shadow-md">
          <div className="relative">
            <img src={images[active]} alt={product.name} className="w-full max-h-[80vh] object-contain rounded-lg" />
            {images.length > 1 && (
              <>
                <button onClick={() => move(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-brand-blue hover:bg-brand-bluedark flex items-center justify-center text-white shadow-md transition-all"><ChevronLeft className="w-6 h-6" /></button>
                <button onClick={() => move(1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-brand-blue hover:bg-brand-bluedark flex items-center justify-center text-white shadow-md transition-all"><ChevronRight className="w-6 h-6" /></button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductDetail;
